import Constants from "../constants/Constants";
import SharedPreferences from "../constants/SharedPreferences";
import SigningPublicKeyTags from "../constants/SigningPublicKeyTags";
import { signWithEcc, verifySignatureWithEcc } from "../crypto/EccSignature";
import { SignatureMetadata } from "../thrift/SignatureMetadata_types";
import { deserializeThrift, serializeThrift } from "../thrift/ThriftFormatter";
import {
  generateSha256Hash,
  getEncryptionCurveFromStorage,
  getFromStorage,
  getSignatureVerificationPublicKey,
  getSigningCurveFromStorage,
  saveInStorage,
} from "./Utility";
import BayunError from "../constants/BayunError";
import { publicKeysMatch } from "../crypto/EccEncryption";

/**
 * Tags whose attestation layers on a member public key are verified against
 * `context + HASH_SEPARATOR + sha256(chain so far)`.
 *
 * All of these were previously six near-identical 40-line branches that differed only by the tag
 * they matched, which meant any fix had to be made six times. Adding a tag here is now the whole
 * change. AuthorizationAppPublicKeyTag is deliberately absent: it is the legacy encryption-key tag
 * and resolves its verification key from a caller-supplied value rather than from the session.
 */
const ATTESTATION_TAGS_VERIFIED_BY_CHAIN_HASH = new Set([
  SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
  SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG,
  SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
  SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
  SigningPublicKeyTags.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY_TAG,
  SigningPublicKeyTags.AUTHORIZATION_APP_SIGNING_PUBLIC_KEY_TAG,
]);

/**
 * Signs a public key string and appends the signature with metadata.
 *
 * @param {String} publicKeyString The public key (possibly with existing signature chain).
 * @param {String} privateKeyString The signing private key in PEM format.
 * @param {String} context The signing context.
 * @param {String} publicKeyTag Tag identifying the signing key type.
 * @param {String} signingCurve The signing curve from storage/API.
 */
export const signDataAndAppendSignatureWithMetadata = async (
  publicKeyString,
  privateKeyString,
  context,
  publicKeyTag,
  signingCurve,
  sessionId,
) => {
  const curve =
    signingCurve ??
    (sessionId ? await getSigningCurveFromStorage(sessionId) : undefined);
  const publicKeyAndSignatures = publicKeyString.split("#");
  const publicKey = publicKeyAndSignatures[0];

  const hash = await generateSha256Hash(publicKeyString);
  const contextWithHash = context + Constants.HASH_SEPARATOR + hash;
  const signedKey = await signData(
    publicKey,
    privateKeyString,
    contextWithHash,
    curve,
  );

  return appendSignatureAndMetadata(
    publicKeyString,
    signedKey,
    publicKeyTag,
    context,
  );
};

/**
 * Signs plaintext with context prefix using the specified curve's signing algorithm.
 *
 * @param {String} plainTextString The plaintext to sign.
 * @param {String} privateKeyString The signing private key in PEM format.
 * @param {String} context The context prepended to plaintext before signing.
 * @param {String} signingCurve The signing curve from storage/API.
 */
export const signData = async (
  plainTextString,
  privateKeyString,
  context,
  signingCurve,
  sessionId,
) => {
  const curve =
    signingCurve ??
    (sessionId ? await getSigningCurveFromStorage(sessionId) : undefined);
  plainTextString = context + "$" + plainTextString;
  return signWithEcc(privateKeyString, plainTextString, curve);
};

/**
 * Verifies a signature on plaintext with context prefix.
 *
 * @param {String} plainText Raw public key whose signature is being verified.
 * @param {String} signature Only the signature, not public key nor metadata.
 * @param {String} publicKey Public key used to verify the signature.
 * @param {String} context The context that was prepended before signing.
 * @param {String} signingCurve The signing curve from storage/API.
 */
export const verifySignature = async (
  plainText,
  signature,
  publicKey,
  context,
  signingCurve,
  sessionId,
) => {
  // Fail closed on missing inputs rather than handing them to the curve implementation.
  //
  // getSignatureVerificationPublicKey resolves every tag through getFromStorage, which returns
  // null when the session never stored that key. Most callers guard the result, but the
  // activate-member chain walk and verifyTrustedMemberPublicKeySignature do not, so a null key
  // reached loadEccSigningPublicKey and surfaced as "Cannot read properties of null (reading
  // 'indexOf')" from inside PEM parsing - a crash that named neither the missing key nor the
  // operation, and that callers checking for a false return never saw as a verification failure.
  if (!publicKey || !signature || plainText === null || plainText === undefined) {
    console.log(
      "Signature verification skipped: missing",
      !publicKey ? "verification key" : !signature ? "signature" : "plaintext",
    );
    return false;
  }

  const curve =
    signingCurve ??
    (sessionId ? await getSigningCurveFromStorage(sessionId) : undefined);
  plainText = context + "$" + plainText;
  return await verifySignatureWithEcc(plainText, signature, publicKey, curve);
};

/**
 *
 * @param {*} messageWithSignatures
 */
export const retrieveAndverifyLastSignature = async (
  sessionId,
  messageWithSignatures,
) => {
  if (!messageWithSignatures) {
    return false;
  }
  //messageWithSignatures's format is message#Signature$metadata#signature$metadata
  const messageAndSignatures = messageWithSignatures.split("#");
  const message = messageAndSignatures[0];
  const lastSignature = messageAndSignatures[messageAndSignatures.length - 1];

  //signature's format is signature+$+metadata
  const signatureAndMetadata = lastSignature.split("$");

  if (signatureAndMetadata.length < 2) {
    return false;
  }

  const signature = signatureAndMetadata[0];
  const signatureMetadataString = signatureAndMetadata[1];
  const signatureMetadata = deserializeThrift(
    signatureMetadataString,
    new SignatureMetadata(),
  );
  const signatureVerificationKey = await getSignatureVerificationPublicKey(
    sessionId,
    signatureMetadata.signatureVerificationKey,
    "",
  );

  if (!message || !signature || !signatureVerificationKey) {
    console.log("signatureVerificationKey", signatureVerificationKey);
    console.log("signature", signature);
    console.log("message", message);
    console.log("context", signatureMetadata.context);
    return false;
  }

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  return await verifySignature(
    message,
    signature,
    signatureVerificationKey,
    signatureMetadata.context,
    signingCurve,
  );
};

/**
 *
 * @param {*} sessionId
 * @param {*} messageWithSignatures
 * @param {*} verificationPublicKey
 * @param {*} publicKeyTag
 */
export const verifyMessageForPublicKeyTag = async (
  sessionId,
  messageWithSignatures,
  verificationPublicKey,
  publicKeyTag,
) => {
  let signature = "";
  let signatureVerificationKey = "";
  let context = "";

  //memberPublicKeyWithSignatures's format is memberPublicKey#Signature$metadata#signature$metadata
  const messageAndSignatures = messageWithSignatures.split("#");
  const message = messageAndSignatures[0];

  for (let i = 1; i < messageAndSignatures.length; i++) {
    //signatureWithMetadata's format is signature+$+metadata
    const signatureWithMetadata = messageAndSignatures[i];
    const signatureAndMetadata = signatureWithMetadata.split("$");
    const signatureMetadataString = signatureAndMetadata[1];
    const signatureMetadata = deserializeThrift(
      signatureMetadataString,
      new SignatureMetadata(),
    );

    if (publicKeyTag === signatureMetadata.signatureVerificationKey) {
      signature = signatureAndMetadata[0];
      context = signatureMetadata.context;

      if (verificationPublicKey) {
        signatureVerificationKey = verificationPublicKey;
      } else {
        signatureVerificationKey = await getSignatureVerificationPublicKey(
          sessionId,
          publicKeyTag,
          "",
        );
      }

      break;
    }
  }

  if (!message || !signature || !signatureVerificationKey || !context) {
    return false;
  }

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  return verifySignature(message, signature, signatureVerificationKey, context, signingCurve);
};

/**
 *
 * @param {*} sessionId
 * @param {*} messageWithSignatures
 * @param {*} verificationPublicKey
 * @param {*} publicKeyTag
 * @param {*} context
 */
export const verifyMessageForPublicKeyTagNewImplementation = async (
  sessionId,
  messageWithSignatures,
  verificationPublicKey,
  publicKeyTag,
  context,
) => {
  let signature = "";
  let signatureVerificationKey = "";
  let contextFromMetadata = "";
  let foundIndex = -1;

  //memberPublicKeyWithSignatures's format is memberPublicKey#Signature$metadata#signature$metadata
  const messageAndSignatures = messageWithSignatures.split("#");
  const message = messageAndSignatures[0];

  for (let i = messageAndSignatures.length - 1; i >= 1; i--) {
    //signatureWithMetadata's format is signature+$+metadata
    const signatureWithMetadata = messageAndSignatures[i];
    const signatureAndMetadata = signatureWithMetadata.split("$");

    if (signatureAndMetadata.length < 2) {
      continue;
    }

    const signatureMetadataString = signatureAndMetadata[1];
    const signatureMetadata = deserializeThrift(
      signatureMetadataString,
      new SignatureMetadata(),
    );

    if (publicKeyTag === signatureMetadata.signatureVerificationKey) {
      signature = signatureAndMetadata[0];
      contextFromMetadata = signatureMetadata.context;
      foundIndex = i;

      if (verificationPublicKey) {
        signatureVerificationKey = verificationPublicKey;
      } else {
        signatureVerificationKey = await getSignatureVerificationPublicKey(
          sessionId,
          publicKeyTag,
          "",
        );
      }

      break;
    }
  }

  if (
    !message ||
    !signature ||
    !signatureVerificationKey ||
    !contextFromMetadata ||
    foundIndex === -1
  ) {
    return false;
  }

  // Calculate hashKey from start of messageAndSignatures to just before the found signature
  // If foundIndex is 1, hashKey is just the message (messageAndSignatures[0])
  // If foundIndex is 2, hashKey is message#signature1
  // etc.
  const hashKeyParts = messageAndSignatures.slice(0, foundIndex);
  const hashKey = hashKeyParts.join(Constants.SIGNATURE_SEPARATOR);

  const hash = await generateSha256Hash(hashKey);

  // Compare raw context with metadata context (metadata stores raw context, not context+hash)
  if (context !== contextFromMetadata) {
    return false;
  }

  const contextWithHash = context + Constants.HASH_SEPARATOR + hash;

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  return await verifySignature(
    message,
    signature,
    signatureVerificationKey,
    contextWithHash,
    signingCurve,
  );
};

/**
 *
 * @param {*} message
 * @param {*} signedMessage
 * @param {*} publicKeyTag
 * @param {*} context
 */
export const appendSignatureAndMetadata = (
  message,
  signedMessage,
  publicKeyTag,
  context,
) => {
  const signatureMetadataObj = new SignatureMetadata();
  signatureMetadataObj.context = context;
  signatureMetadataObj.signatureVerificationKey = publicKeyTag;

  const signatureMetadata = serializeThrift(signatureMetadataObj);

  return `${message}#${signedMessage}$${signatureMetadata}`;
};

export const verifyTrustedSignatures = async (
  sessionId,
  message,
  // trustedSignatureWithMetadata
  trustedSignature,
) => {
  //signature's format is signature+$+metadata
  // const signatureAndMetadata = trustedSignatureWithMetadata.split("$");

  // if (signatureAndMetadata.length < 2) {
  //   return false;
  // }

  // const signature = signatureAndMetadata[0];
  // const signatureMetadataString = signatureAndMetadata[1];
  // const signatureMetadata = deserializeThrift(
  //   signatureMetadataString,
  //   new SignatureMetadata()
  // );
  // const signatureVerificationKey = await getSignatureVerificationPublicKey(
  //   sessionId,
  //   signatureMetadata.signatureVerificationKey,
  //   ""
  // );

  const signatureVerificationKey = await getSignatureVerificationPublicKey(
    sessionId,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    "",
  );

  // Previous call (fetching from storage inside the function):
  // const context = await getMemberPublicKeyContext(sessionId);
  // New call (fetching from storage at call site and passing as parameters):
  const context = await getMemberPublicKeyContext(
    await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
    await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
  );

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  return await verifySignature(
    message,
    trustedSignature,
    signatureVerificationKey,
    // getMemberPublicKeyContext(sessionId),
    context,
    signingCurve,
  );
};

/**
 * Verification keys supplied by the caller, keyed by the tag they verify.
 *
 * Every other tag resolves out of the verifying session's own storage, which is correct for keys
 * the session owns - its member key, its org's admin key, the server key. It is wrong for the two
 * app layers: those are signed by the app the member registered on, so a session holding a
 * different app's keys resolves the wrong key or none at all. The server sends the right ones with
 * the member, and this is where they enter verification.
 *
 * @param {Object} attestingAppSigningPublicKeys Map of tag to PEM. Absent entries fall back to the
 *   session, which keeps every caller that has no such keys working unchanged.
 * @returns {Function} Resolver taking a tag and returning the key to verify that layer with.
 */
const buildVerificationKeyResolver = (
  sessionId,
  authorizationAppPublicKey,
  attestingAppSigningPublicKeys,
) => {
  const supplied = attestingAppSigningPublicKeys || {};
  return async (tag) => {
    // Legacy authorization app layer: signed with the app's encryption key before the split, and
    // already verified from a caller-supplied key. Kept for chains written by older deployments.
    if (tag === SigningPublicKeyTags.AUTHORIZATION_APP_PUBLIC_KEY_TAG) {
      return authorizationAppPublicKey
        ? getMessageFromSignatureString(authorizationAppPublicKey)
        : await getSignatureVerificationPublicKey(sessionId, tag, "");
    }
    const suppliedKey = supplied[tag];
    if (suppliedKey) {
      return getMessageFromSignatureString(suppliedKey);
    }
    return await getSignatureVerificationPublicKey(sessionId, tag, "");
  };
};

export const verifyMemberPublicKey = async (
  sessionId,
  memberPublicKey,
  memberPublicKeyVerificationPurpose,
  trustedMemberPublicKeySignature,
  orgName,
  orgMemberId,
  memberStatus,
  authorizationAppPublicKey,
  backdoorPublicKey,
  adminPublicKeyForGroupOperation,
  attestingAppSigningPublicKeys,
) => {
  const contextLocal = await getMemberPublicKeyContext(orgName, orgMemberId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const keyValidationPolicy = await getFromStorage(sessionId, SharedPreferences.KEY_VALIDATION_POLICY);
  const resolveVerificationKey = buildVerificationKeyResolver(
    sessionId,
    authorizationAppPublicKey,
    attestingAppSigningPublicKeys,
  );
  if (
    memberPublicKeyVerificationPurpose ==
    Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.MEMBER_LOCK_BOX
  ) {
    if (memberStatus == Constants.MEMBER_STATUS.AUTO_APPROVED) {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          memberPublicKey,
          "",
          SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        console.log("verification failed for auto approved member");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          memberPublicKey,
          "",
          SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        console.log("verification failed for admin member");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }
  } else if (
    memberPublicKeyVerificationPurpose ==
    Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.ACTIVATE_MEMBER || (memberPublicKeyVerificationPurpose == Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.RECOVER_USER && memberStatus == Constants.MEMBER_STATUS.REGISTERED) || (memberPublicKeyVerificationPurpose == Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.RECOVER_USER && memberStatus == Constants.MEMBER_STATUS.CANCELLED)
  ) {
    const publicKeyAndSignatures = memberPublicKey.split("#");
    const publicKeyToBeVerified = publicKeyAndSignatures[0];
    let countOfSignaturesVerified = 0;

    for (let i = publicKeyAndSignatures.length - 1; i >= 1; i--) {
      const signatureWithMetadata = publicKeyAndSignatures[i];
      const signatureAndMetadata = signatureWithMetadata.split("$");
      const signatureMetadataString = signatureAndMetadata[1];
      const signatureMetadata = deserializeThrift(
        signatureMetadataString,
        new SignatureMetadata(),
      );

      const tag = signatureMetadata.signatureVerificationKey;
      if (!ATTESTATION_TAGS_VERIFIED_BY_CHAIN_HASH.has(tag)) {
        // A tag this branch does not attest with contributes nothing to the count. The legacy
        // AuthorizationAppPublicKeyTag below is the one exception, because its verification key
        // comes from the caller rather than from the session.
        if (tag !== SigningPublicKeyTags.AUTHORIZATION_APP_PUBLIC_KEY_TAG) {
          continue;
        }
        // Legacy authorization app encryption-key tag (pre separate-signing deployments).
        if (
          authorizationAppPublicKey &&
          !(await retrieveAndverifyLastSignature(
            sessionId,
            authorizationAppPublicKey,
          ))
        ) {
          console.log("appPublicKey verification failed.");
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }
      }

      if (contextLocal !== signatureMetadata.context) {
        // Metadata stores the raw context; the signature covers context + hash of the chain so
        // far. A mismatch here means the layer was made for a different member or org.
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      const signatureVerificationKey = await resolveVerificationKey(tag);

      if (!signatureVerificationKey) {
        // Name the tag. An absent key means neither the member nor this session carried the key
        // that tag stands for, which is a different problem from a signature that does not check
        // out, and the two are indistinguishable once both are ERROR_MEMBER_VERIFICATION_FAILED.
        console.log(
          `No verification key for tag ${tag}; cannot verify member public key.`,
        );
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      const hashKey = publicKeyAndSignatures
        .slice(0, i)
        .join(Constants.SIGNATURE_SEPARATOR);
      const contextWithHash =
        contextLocal +
        Constants.HASH_SEPARATOR +
        (await generateSha256Hash(hashKey));

      if (
        !(await verifySignature(
          publicKeyToBeVerified,
          signatureAndMetadata[0],
          signatureVerificationKey,
          contextWithHash,
          signingCurve,
        ))
      ) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      countOfSignaturesVerified++;

      if (
        memberStatus == Constants.MEMBER_STATUS.REGISTERED &&
        countOfSignaturesVerified >= 2
      ) {
        break;
      } else if (
        memberStatus != Constants.MEMBER_STATUS.REGISTERED &&
        countOfSignaturesVerified >= 3
      ) {
        break;
      }
    }

    if (
      (memberStatus == Constants.MEMBER_STATUS.REGISTERED &&
        countOfSignaturesVerified < 2) ||
      (memberStatus != Constants.MEMBER_STATUS.REGISTERED &&
        countOfSignaturesVerified < 3)
    ) {
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    if (trustedMemberPublicKeySignature) {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          trustedMemberPublicKeySignature,
          "",
          SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        console.log("verification failed for activate member");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }
  } else if (
    memberPublicKeyVerificationPurpose ==
    Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.TRANSACTION
  ) {
    if (trustedMemberPublicKeySignature) {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          trustedMemberPublicKeySignature,
          "",
          SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        console.log("verification failed for security admin or transaction");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      if (memberStatus == Constants.MEMBER_STATUS.AUTO_APPROVED && keyValidationPolicy == Constants.KEY_VALIDATION_POLICY.AUTO_APPROVAL) {
        // verify using server public key
        if (
          !(await verifyMessageForPublicKeyTagNewImplementation(
            sessionId,
            memberPublicKey,
            "",
            SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
            contextLocal,
          ))
        ) {
          console.log("verification failed for bayun server public key");
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }
        return;
      } else if (memberStatus == Constants.MEMBER_STATUS.AUTO_APPROVED && keyValidationPolicy != Constants.KEY_VALIDATION_POLICY.AUTO_APPROVAL) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      } else if (
          !(await verifyMessageForPublicKeyTagNewImplementation(
            sessionId,
            memberPublicKey,
            "",
            SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
            contextLocal,
          ))
        ) {
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }

      // if (
      //   !(await verifyMessageForPublicKeyTagNewImplementation(
      //     sessionId,
      //     memberPublicKey,
      //     backdoorPublicKey,
      //     SigningPublicKeyTags.BACKDOOR_PUBLIC_KEY_TAG,
      //     contextLocal,
      //   ))
      // ) {
      //   console.log("verification failed for backdoor public key");
      //   throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      // }
    }
  } else if (
    memberPublicKeyVerificationPurpose ==
    Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION
  ) {
    const storedOrgName = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_NAME,
    );

    const storeOrgMemberId = await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID);
    // Both org and member must match to treat this as "myself". orgMemberId alone is not unique
    // across orgs (values like "admin" or an employee number recur), so comparing it in isolation
    // let a peer from another org with a colliding orgMemberId skip verification entirely.
    if (storeOrgMemberId === orgMemberId && storedOrgName === orgName) {
      return;
    }

    if (trustedMemberPublicKeySignature) {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          trustedMemberPublicKeySignature,
          "",
          SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        console.log("verification failed for group operation");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      return;
    }

    if (keyValidationPolicy == Constants.KEY_VALIDATION_POLICY.AUTO_APPROVAL) {
        // verify using server public key
        if (
          !(await verifyMessageForPublicKeyTagNewImplementation(
            sessionId,
            memberPublicKey,
            "",
            SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
            contextLocal,
          ))
        ) {
          console.log("verification failed for bayun server public key");
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }
        return;
    }

    if (orgName != storedOrgName) {
      // Cross-org group operation: the peer belongs to another org, so their member public key
      // is attested by that org's admin rather than ours.
      //
      // The foreign admin signing key arrives as a chain signed by the Bayun server signing key
      // (which the client pins), so verify that chain before trusting it. Without this the
      // server could hand over an admin key it generated itself, sign the peer's member public
      // key with it, and the check below would pass against an adversary-chosen key.
      if (!adminPublicKeyForGroupOperation) {
        console.log("Cross-org admin signing public key not provided.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          adminPublicKeyForGroupOperation,
        ))
      ) {
        console.log("Cross-org admin signing public key verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      // A valid server attestation is not enough: every org's admin signing key is
      // server-attested by construction, so without pinning the context to this peer's org, org
      // B's admin could attest a member public key whose context claims org C. Must match the
      // LMS SignatureUtil.getContextForAdminSigningPublicKey.
      const expectedAdminSigningContext = `${Constants.ADMIN_SIGNING_PUBLIC_KEY}${Constants.CONTEXT_SEPARATOR}${orgName}`;
      if (
        getLastSignatureContext(adminPublicKeyForGroupOperation) !==
        expectedAdminSigningContext
      ) {
        console.log(
          "Cross-org admin signing public key attested for a different org.",
        );
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      const crossOrgAdminSigningPublicKey = getMessageFromSignatureString(
        adminPublicKeyForGroupOperation,
      );
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          memberPublicKey,
          crossOrgAdminSigningPublicKey,
          SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          memberPublicKey,
          "",
          SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }
  } else if (
    memberPublicKeyVerificationPurpose ==
    Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.RECOVER_USER &&
    memberStatus == Constants.MEMBER_STATUS.AUTO_APPROVED
  ) {
    if (
      !(await verifyMessageForPublicKeyTagNewImplementation(
        sessionId,
        memberPublicKey,
        "",
        SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG,
        contextLocal,
      ))
    ) {
      console.log(
        "verification failed for ghost member public key during user recovery",
      );
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    if (
      !(await verifyMessageForPublicKeyTagNewImplementation(
        sessionId,
        memberPublicKey,
        "",
        SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
        contextLocal,
      ))
    ) {
      console.log(
        "verification failed for bayun server public key during user recovery",
      );
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
  } else {
    if (trustedMemberPublicKeySignature) {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          trustedMemberPublicKeySignature,
          "",
          SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      if (
        !(await verifyMessageForPublicKeyTagNewImplementation(
          sessionId,
          memberPublicKey,
          "",
          SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
          contextLocal,
        ))
      ) {
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }
  }
};

export const retrieveAndverifyLastSignatureNewImplementation = async (
  sessionId,
  messageWithSignatures,
  context,
) => {
  const publicKeyAndSignatures = messageWithSignatures.split("#");
  const publicKeyToBeVerified = publicKeyAndSignatures[0];
  const lastSignatureWithMetadata =
    publicKeyAndSignatures[publicKeyAndSignatures.length - 1];
  const lastSignatureAndMetadata = lastSignatureWithMetadata.split("$");
  if (lastSignatureAndMetadata.length < 2) {
    return false;
  }
  const lastSignature = lastSignatureAndMetadata[0];
  const lastSignatureMetadata = lastSignatureAndMetadata[1];
  const deserializedLastSignatureMetadata = deserializeThrift(
    lastSignatureMetadata,
    new SignatureMetadata(),
  );

  const signatureVerificationKey = await getSignatureVerificationPublicKey(
    sessionId,
    deserializedLastSignatureMetadata.signatureVerificationKey,
    "",
  );

  const contextFromMetadata = deserializedLastSignatureMetadata.context;

  // Compare raw context with metadata context (metadata stores raw context, not context+hash)
  if (context !== contextFromMetadata) {
    return false;
  }

  const hashKey = messageWithSignatures.substring(
    0,
    messageWithSignatures.lastIndexOf(Constants.SIGNATURE_SEPARATOR),
  );

  const hash = await generateSha256Hash(hashKey);

  const contextWithHash = context + Constants.HASH_SEPARATOR + hash;

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  return await verifySignature(
    publicKeyToBeVerified,
    lastSignature,
    signatureVerificationKey,
    contextWithHash,
    signingCurve,
  );
};

/**
 *
 * @param {*} messageWithSignatures
 */
export const getMessageFromSignatureString = (messageWithSignatures) => {
  // the format is id#Signature$metadata#signature$metadata
  const messageAndSignatures = messageWithSignatures.split("#");
  return messageAndSignatures[0];
};

/**
 * Returns the context recorded in the last signature layer's metadata, or null when the string
 * carries no parseable layer.
 *
 * Callers use this to assert that a server-issued attestation was made for the subject they are
 * about to trust it for. Verifying that a chain is well-signed says nothing about what it attests.
 */
export const getLastSignatureContext = (messageWithSignatures) => {
  if (!messageWithSignatures) {
    return null;
  }
  const parts = messageWithSignatures.split(Constants.SIGNATURE_SEPARATOR);
  if (parts.length < 2) {
    return null;
  }
  const signatureAndMetadata = parts[parts.length - 1].split("$");
  if (signatureAndMetadata.length < 2) {
    return null;
  }
  try {
    const metadata = deserializeThrift(
      signatureAndMetadata[1],
      new SignatureMetadata(),
    );
    return metadata?.context ?? null;
  } catch (error) {
    return null;
  }
};

/**
 * Removes signature layers whose metadata matches the given public key tag.
 * @param {*} messageWithSignatures
 * @param {*} publicKeyTag
 */
export const removeSignatureLayersForPublicKeyTag = (
  messageWithSignatures,
  publicKeyTag,
) => {
  const parts = messageWithSignatures.split("#");
  if (parts.length <= 1) {
    return messageWithSignatures;
  }

  const kept = [];
  for (let i = 1; i < parts.length; i++) {
    const signatureAndMetadata = parts[i].split("$");
    if (signatureAndMetadata.length < 2) {
      kept.push(parts[i]);
      continue;
    }
    const signatureMetadata = deserializeThrift(
      signatureAndMetadata[1],
      new SignatureMetadata(),
    );
    if (signatureMetadata.signatureVerificationKey !== publicKeyTag) {
      kept.push(parts[i]);
    }
  }

  return kept.length === 0 ? parts[0] : `${parts[0]}#${kept.join("#")}`;
};

/**
 * Verifies a signed admin public key and persists the stripped key if not already stored.
 *
 * @param {String} [memberSigningPublicKeyOverride] Member signing public key to verify with.
 *   Callers that have not yet persisted the member signing key to the session (lockbox load
 *   verifies the admin key before saving it) must pass it explicitly.
 * @returns {Promise<boolean>} true if verified and saved; false if skipped/deferred
 */
export const verifyAndSaveAdminPublicKey = async (
  sessionId,
  adminPublicKeyWithSignatures,
  memberStatus,
  options = {},
  memberSigningPublicKeyOverride,
) => {
  const existing = await getFromStorage(
    sessionId,
    SharedPreferences.ADMIN_PUBLIC_KEY,
  );
  if (existing || !adminPublicKeyWithSignatures) {
    return false;
  }

  let verified = false;

  if (
    memberStatus === Constants.MEMBER_STATUS.APPROVED ||
    memberStatus === Constants.MEMBER_STATUS.ADMIN ||
    memberStatus === Constants.MEMBER_STATUS.SECURITY_ADMIN
  ) {
    const memberSigningPublicKey =
      (memberSigningPublicKeyOverride
        ? getMessageFromSignatureString(memberSigningPublicKeyOverride)
        : null) ||
      (await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
      ));
    if (!memberSigningPublicKey) {
      if (options.deferIfMemberPublicKeyMissing) {
        return false;
      }
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    verified = await verifyMessageForPublicKeyTag(
      sessionId,
      adminPublicKeyWithSignatures,
      memberSigningPublicKey,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    );
  } else {
    verified = await retrieveAndverifyLastSignature(
      sessionId,
      adminPublicKeyWithSignatures,
    );
  }

  if (!verified) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  await saveInStorage(
    sessionId,
    SharedPreferences.ADMIN_PUBLIC_KEY,
    getMessageFromSignatureString(adminPublicKeyWithSignatures),
  );
  return true;
};

/**
 * Verifies a signed backdoor public key and persists the stripped key if not already stored.
 * @returns {Promise<boolean>} true if verified and saved; false if skipped/deferred
 */
export const verifyAndSaveBackdoorPublicKey = async (
  sessionId,
  backdoorPublicKeyWithSignatures,
) => {
  const existing = await getFromStorage(
    sessionId,
    SharedPreferences.BACKDOOR_PUBLIC_KEY,
  );
  if (existing || !backdoorPublicKeyWithSignatures) {
    return false;
  }

  // The backdoor public key layer is produced with the admin signing key and tagged
  // AdminSigningPublicKeyTag, so it must be verified with that key and tag.
  const adminSigningPublicKey = getMessageFromSignatureString(
    (await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    )) || "",
  );
  if (!adminSigningPublicKey) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  const verified = await verifyMessageForPublicKeyTag(
    sessionId,
    backdoorPublicKeyWithSignatures,
    adminSigningPublicKey,
    SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
  );

  if (!verified) {
    console.log("backdoorPublicKey verification failed.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  await saveInStorage(
    sessionId,
    SharedPreferences.BACKDOOR_PUBLIC_KEY,
    getMessageFromSignatureString(backdoorPublicKeyWithSignatures),
  );
  return true;
};

export const verifyMemberPublicKeyOwnership = async (
  memberPrivateKeyPem,
  memberPublicKeyWithSignatures,
  sessionId,
) => {
  const rawPublicKey = getMessageFromSignatureString(
    memberPublicKeyWithSignatures,
  );
  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  if (
    !(await publicKeysMatch(memberPrivateKeyPem, rawPublicKey, encryptionCurve))
  ) {
    console.log("memberPublicKey ownership verification failed.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
};

// Signing context getters
export const getMemberAppPublicKeyContext = async (sessionId) =>
  `memberAppPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  )}${await getFromStorage(sessionId, SharedPreferences.APP_ID)}`;

export const getBackdoorPublicKeyContext = async (sessionId) =>
  `backdoorPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}`;

// Layer-2 contexts bind the previous layer's signature, always delimited by
// Constants.CONTEXT_SEPARATOR. Undelimited concatenation is ambiguous: `ctx + sigA + sigB` cannot
// be distinguished from `ctx + sigAsigB`. The LMS SignatureUtil.appendPreviousSignature helper
// must produce the identical string.
export const getBackdoorPublicKeyContextLayer2 = async (
  sessionId,
  previousSignature,
) =>
  `${await getBackdoorPublicKeyContext(sessionId)}${Constants.CONTEXT_SEPARATOR}${previousSignature}`;

export const getMemberAppPublicKeyContextLayer2 = async (
  sessionId,
  previousSignature,
) =>
  `${await getMemberAppPublicKeyContext(sessionId)}${Constants.CONTEXT_SEPARATOR}${previousSignature}`;

export const getMemberPublicKeyContext = async (orgName, orgMemberId) =>
  `memberPublicKey$${orgName}${orgMemberId}`;

export const getOrgAppApprovalContext = async (orgName, appId) =>
  `orgAppApproval$${orgName}$${appId}`;

export const messageHasPublicKeyTag = (messageWithSignatures, publicKeyTag) => {
  if (!messageWithSignatures) {
    return false;
  }
  const messageAndSignatures = messageWithSignatures.split("#");
  for (let i = 1; i < messageAndSignatures.length; i++) {
    const signatureAndMetadata = messageAndSignatures[i].split("$");
    if (signatureAndMetadata.length < 2) {
      continue;
    }
    const signatureMetadata = deserializeThrift(
      signatureAndMetadata[1],
      new SignatureMetadata(),
    );
    if (signatureMetadata.signatureVerificationKey === publicKeyTag) {
      return true;
    }
  }
  return false;
};

const getContextForPublicKeyTag = (messageWithSignatures, publicKeyTag) => {
  if (!messageWithSignatures) {
    return null;
  }
  const messageAndSignatures = messageWithSignatures.split("#");
  for (let i = 1; i < messageAndSignatures.length; i++) {
    const signatureAndMetadata = messageAndSignatures[i].split("$");
    if (signatureAndMetadata.length < 2) {
      continue;
    }
    const signatureMetadata = deserializeThrift(
      signatureAndMetadata[1],
      new SignatureMetadata(),
    );
    if (signatureMetadata.signatureVerificationKey === publicKeyTag) {
      return signatureMetadata.context;
    }
  }
  return null;
};

export const verifyOrgAppApprovalSignature = async (
  sessionId,
  signedAppId,
  appId,
  orgName,
) => {
  if (!signedAppId) {
    return true;
  }
  const message = getMessageFromSignatureString(signedAppId);
  if (message !== appId) {
    return false;
  }
  const expectedContext = await getOrgAppApprovalContext(orgName, appId);
  const verifySimpleTag = async (publicKeyTag, verificationPublicKey) => {
    if (getContextForPublicKeyTag(signedAppId, publicKeyTag) !== expectedContext) {
      return false;
    }
    return verifyMessageForPublicKeyTag(
      sessionId,
      signedAppId,
      verificationPublicKey,
      publicKeyTag,
    );
  };
  if (
    messageHasPublicKeyTag(
      signedAppId,
      SigningPublicKeyTags.ADMIN_PUBLIC_KEY_TAG,
    )
  ) {
    const adminPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    );
    return verifySimpleTag(
      SigningPublicKeyTags.ADMIN_PUBLIC_KEY_TAG,
      adminPublicKey,
    );
  }
  if (
    messageHasPublicKeyTag(
      signedAppId,
      SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG,
    )
  ) {
    // The signing half, under its current name. GHOST_MEMBER_PUBLIC_KEY no longer exists here -
    // it was renamed when the ghost key pair was split - so this read resolved to undefined and
    // the check could only ever fail.
    const ghostPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY,
    );
    return verifySimpleTag(
      SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG,
      ghostPublicKey,
    );
  }
  return false;
};

export const getAdminPublicKeyContext = async (sessionId) =>
  `adminPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}`;

export const getGhostMemberIdContext = async (sessionId) =>
  `ghostMemberId$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getGhostMemberPublicKeyContext = async (sessionId) =>
  `ghostMemberPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.GHOST_MEMBER_ID,
  )}`;

export const getOrgKeyContext = async (sessionId) =>
  `orgKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getOrgPrivateKeyContext = async (sessionId) =>
  `orgPrivateKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const ORG_SETTINGS_CANONICAL_KEYS = [
  "appApproval",
  "encryptionMode",
  "encryptionPolicy",
  "encryptionStatus",
  "keyGenerationPolicy",
  "keyValidationPolicy",
  "lockboxExpiry",
  "minimumApprovalCount",
  "statsSyncTime",
  "memberApproval",
];

export const buildOrgSettingsCanonical = (plainSettings) =>
  ORG_SETTINGS_CANONICAL_KEYS.map(
    (key) => `${key}=${plainSettings[key] ?? ""}`,
  ).join("&");

export const ORG_SETTINGS_LAST_UPDATED_AT_KEYS = [
  "minimumApprovalCountLastUpdatedAt",
  "memberApprovalLastUpdatedAt",
  "appApprovalLastUpdatedAt",
  "keyValidationPolicyLastUpdatedAt",
  "encryptionPolicyLastUpdatedAt",
  "keyGenerationPolicyLastUpdatedAt",
  "lockboxExpiryLastUpdatedAt",
  "statsSyncTimeLastUpdatedAt",
  "encryptionStatusLastUpdatedAt",
  "encryptionModeLastUpdatedAt",
];

export const buildOrgSettingsTimestampsCanonical = (orgSettingsLastUpdatedAt) =>
  ORG_SETTINGS_LAST_UPDATED_AT_KEYS.map((key) =>
    String(orgSettingsLastUpdatedAt?.[key] ?? ""),
  ).join("#");

/**
 * Org settings that travel to the client as plaintext rather than AEAD-encrypted values.
 *
 * These select security behaviour, so they must be covered by the admin signature. In
 * particular `keyValidationPolicy = AutoApproval` downgrades member public key validation from
 * "the org admin must attest" to "the server attests itself" (see verifyMemberPublicKey's
 * GROUP_OPERATION branch), so an unsigned value here is a policy-downgrade vector.
 *
 * Must stay in this order, and identical to ORG_SETTINGS_AUTHENTICATED_PLAINTEXT_KEYS in the LMS
 * SignatureUtil.java — a one-sided change fails every org-settings write.
 */
export const ORG_SETTINGS_AUTHENTICATED_PLAINTEXT_KEYS = [
  "keyValidationPolicy",
  "memberApproval",
  "appApproval",
];

export const buildOrgSettingsAuthenticatedPlaintextCanonical = (settings) =>
  ORG_SETTINGS_AUTHENTICATED_PLAINTEXT_KEYS.map(
    (key) => `${key}=${settings?.[key] ?? ""}`,
  ).join("&");

/**
 * Canonical form covering both the freshness timestamps and the plaintext policy values.
 *
 * Signing the timestamps alone let a server pair current timestamps with tampered or stale
 * policy values, because nothing bound the two together.
 */
export const buildOrgSettingsSignedCanonical = (
  orgSettingsLastUpdatedAt,
  authenticatedPlaintextSettings,
) =>
  `${buildOrgSettingsTimestampsCanonical(orgSettingsLastUpdatedAt)}|` +
  `${buildOrgSettingsAuthenticatedPlaintextCanonical(authenticatedPlaintextSettings)}`;

export const createInitialOrgSettingsLastUpdatedAt = (
  timestamp = Date.now(),
) =>
  ORG_SETTINGS_LAST_UPDATED_AT_KEYS.reduce((orgSettingsLastUpdatedAt, key) => {
    orgSettingsLastUpdatedAt[key] = timestamp;
    return orgSettingsLastUpdatedAt;
  }, {});

export const isOrgSettingsLastUpdatedAtObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

export const getMaxOrgSettingsLastUpdatedAt = (orgSettingsLastUpdatedAt) => {
  if (!isOrgSettingsLastUpdatedAtObject(orgSettingsLastUpdatedAt)) {
    return null;
  }

  const values = ORG_SETTINGS_LAST_UPDATED_AT_KEYS.map((key) =>
    Number(orgSettingsLastUpdatedAt[key]),
  ).filter(Number.isFinite);

  return values.length ? Math.max(...values) : null;
};

export const assertNoOrgSettingsRollback = (localStored, incoming) => {
  if (localStored == null || localStored === "") {
    return;
  }

  if (isOrgSettingsLastUpdatedAtObject(localStored)) {
    for (const key of ORG_SETTINGS_LAST_UPDATED_AT_KEYS) {
      const localTs = localStored[key];
      const incomingTs = incoming[key];

      if (
        localTs != null &&
        localTs !== "" &&
        Number.isFinite(Number(localTs)) &&
        Number.isFinite(Number(incomingTs)) &&
        Number(incomingTs) < Number(localTs)
      ) {
        throw BayunError.ERROR_ORG_SETTINGS_ROLLED_BACK;
      }
    }
    return;
  }

  const localTs = Number(localStored);
  const incomingMax = getMaxOrgSettingsLastUpdatedAt(incoming);

  if (
    Number.isFinite(localTs) &&
    incomingMax != null &&
    incomingMax < localTs
  ) {
    throw BayunError.ERROR_ORG_SETTINGS_ROLLED_BACK;
  }
};

export const getSettingLastUpdatedAtStorageKey = (orgName) =>
  `${orgName}#${SharedPreferences.SETTING_LAST_UPDATED_AT}`;

export const getOrgSettingsContext = async (sessionId, orgName) => {
  const resolvedOrgName =
    orgName ?? (await getFromStorage(sessionId, SharedPreferences.ORG_NAME));
  return `orgSettings$${resolvedOrgName}`;
};

/**
 * Reads the admin signing private key, failing with a named error when it is absent.
 *
 * Signing sites elsewhere go through a required-getter; these two read storage directly, so an
 * absent key reached signWithEcc as undefined and threw from inside the curve implementation with
 * nothing naming the missing key.
 */
const requireAdminSigningPrivateKey = async (sessionId) => {
  const key = await getFromStorage(
    sessionId,
    SharedPreferences.ADMIN_SIGNING_PRIVATE_KEY,
  );
  if (!key) {
    throw new Error("Admin signing private key is required.");
  }
  return key;
};

export const signOrgSettings = async (
  plainSettings,
  _adminPrivateKey,
  sessionId,
) => {
  const settingLastUpdatedAt = Date.now();
  const canonical = buildOrgSettingsCanonical(plainSettings);
  const context = await getOrgSettingsContext(sessionId);
  const adminSigningPrivateKey = await requireAdminSigningPrivateKey(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const signedSettings = await signData(
    canonical,
    adminSigningPrivateKey,
    context,
    signingCurve,
    sessionId,
  );
  return { signedSettings, settingLastUpdatedAt };
};

export const signOrgSettingsFromLastUpdatedAt = async (
  orgSettingsLastUpdatedAt,
  _adminPrivateKey,
  sessionId,
  orgName,
  authenticatedPlaintextSettings,
) => {
  const canonical = buildOrgSettingsSignedCanonical(
    orgSettingsLastUpdatedAt,
    authenticatedPlaintextSettings,
  );
  const context = await getOrgSettingsContext(sessionId, orgName);
  const adminSigningPrivateKey = await requireAdminSigningPrivateKey(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const signedSettings = await signData(
    canonical,
    adminSigningPrivateKey,
    context,
    signingCurve,
    sessionId,
  );
  return { signedSettings, orgSettingsLastUpdatedAt };
};

export const verifyOrgSettingsFromLastUpdatedAt = async (
  signedSettings,
  orgSettingsLastUpdatedAt,
  _adminPublicKey,
  sessionId,
  orgName,
  authenticatedPlaintextSettings,
) => {
  if (!signedSettings || orgSettingsLastUpdatedAt == null) {
    return false;
  }

  const canonical = buildOrgSettingsSignedCanonical(
    orgSettingsLastUpdatedAt,
    authenticatedPlaintextSettings,
  );
  const context = await getOrgSettingsContext(sessionId, orgName);
  const adminSigningPublicKey = getMessageFromSignatureString(
    (await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    )) || "",
  );
  if (!adminSigningPublicKey) {
    console.log("Admin signing public key not found for org settings.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  return verifySignature(
    canonical,
    signedSettings,
    adminSigningPublicKey,
    context,
    signingCurve,
    sessionId,
  );
};

export const verifyAndPersistOrgSettingsFromLastUpdatedAt = async (
  signedSettings,
  orgSettingsLastUpdatedAt,
  sessionId,
  adminPublicKey,
  orgName,
  authenticatedPlaintextSettings,
) => {
  if (!signedSettings || orgSettingsLastUpdatedAt == null) {
    return;
  }

  const storageKey = getSettingLastUpdatedAtStorageKey(orgName);
  const localStored = await getFromStorage(sessionId, storageKey);

  assertNoOrgSettingsRollback(localStored, orgSettingsLastUpdatedAt);

  const valid = await verifyOrgSettingsFromLastUpdatedAt(
    signedSettings,
    orgSettingsLastUpdatedAt,
    adminPublicKey,
    sessionId,
    orgName,
    authenticatedPlaintextSettings,
  );
  if (!valid) {
    throw BayunError.ERROR_ORG_SETTINGS_SIGNATURE_INVALID;
  }

  await saveInStorage(sessionId, storageKey, orgSettingsLastUpdatedAt);
};

export const verifyOrgSettings = async (
  plainSettings,
  signedSettings,
  settingLastUpdatedAt,
  _adminPublicKey,
  sessionId,
) => {
  if (!signedSettings || settingLastUpdatedAt == null) {
    return false;
  }
  const canonical = buildOrgSettingsCanonical(plainSettings);
  const context = await getOrgSettingsContext(sessionId);
  const adminSigningPublicKey = getMessageFromSignatureString(
    (await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    )) || "",
  );
  if (!adminSigningPublicKey) {
    console.log("Admin signing public key not found for org settings.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  return verifySignature(
    canonical,
    signedSettings,
    adminSigningPublicKey,
    context,
    signingCurve,
    sessionId,
  );
};

export const verifyAndPersistOrgSettings = async (
  plainSettings,
  signedSettings,
  settingLastUpdatedAt,
  sessionId,
  adminPublicKey,
) => {
  if (!signedSettings) {
    return;
  }

  const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);
  const storageKey = getSettingLastUpdatedAtStorageKey(orgName);
  const localStored = await getFromStorage(sessionId, storageKey);
  const localTs = localStored != null ? Number(localStored) : null;

  if (Number.isFinite(localTs) && localTs > Number(settingLastUpdatedAt)) {
    throw BayunError.ERROR_ORG_SETTINGS_ROLLED_BACK;
  }

  const valid = await verifyOrgSettings(
    plainSettings,
    signedSettings,
    settingLastUpdatedAt,
    adminPublicKey,
    sessionId,
  );
  if (!valid) {
    throw BayunError.ERROR_ORG_SETTINGS_SIGNATURE_INVALID;
  }

  await saveInStorage(
    sessionId,
    storageKey,
    String(settingLastUpdatedAt),
  );
};

export const getMemberKeyKekContext = async (sessionId) =>
  `memberKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getUserKeyKekContext = async (sessionId) =>
  `userKeyKek$${await getFromStorage(sessionId, SharedPreferences.USER_ID)}`;

/**
 * The user-key KEK context for a named user rather than for whoever is signed in.
 *
 * Account recovery signs the recovered user's key KEK from the administrator's session, where
 * USER_ID is the administrator's. The value it produced was therefore bound to the wrong identity
 * and persisted that way into the recovered user's key detail, so this variant takes the user it
 * is actually for. It must build the same string the recovered user's own session would.
 *
 * @param {String} userId The user the KEK belongs to.
 */
export const getUserKeyKekContextForUser = (userId) => `userKeyKek$${userId}`;

export const getRecoveryKeyKekContext = async (sessionId) =>
  `recoveryKeyKek$${await getFromStorage(sessionId, SharedPreferences.USER_ID)}`;

export const getMemberSigningPrivateKeyKekContext = async (sessionId) =>
  `memberSigningPrivateKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getMemberSigningPublicKeyContext = async (sessionId) =>
  `memberSigningPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getMemberSigningPublicKeyContextLayer2 = async (
  sessionId,
  previousSignature,
) =>
  `${await getMemberSigningPublicKeyContext(sessionId)}${Constants.CONTEXT_SEPARATOR}${previousSignature}`;

export const getOrgPublicKeyContext = async (sessionId) =>
  `orgPublicKey$${await getFromStorage(sessionId, SharedPreferences.ORG_NAME)}`;

export const getOrgPublicKeyContextLayer2 = async (
  sessionId,
  previousSignature,
) =>
  `${await getOrgPublicKeyContext(sessionId)}${Constants.CONTEXT_SEPARATOR}${previousSignature}`;

export const getMemberPrivateKeyInnerKekContext = async (sessionId) =>
  `memberPrivateKeyInnerKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  )}${await getFromStorage(sessionId, SharedPreferences.APP_ID)}`;

export const getOrgKeyKekAdminOrgContext = async (sessionId) =>
  `orgKeyKek$${await getFromStorage(sessionId, SharedPreferences.ORG_NAME)}`;

export const getOrgKeyKekMemberLockboxContext = async (sessionId) =>
  `orgKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getOrgPrivateKeyKekContext = async (sessionId) =>
  `orgPrivateKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getBackdoorPrivateKeyPartKekContext = async (
  orgName,
  orgMemberId,
) => `backdoorPrivateKeyPartKek$${orgName}${orgMemberId}`;

export const getAdminPrivateKeyKekContext = async (orgName, orgMemberId) =>
  `adminPrivateKeyKek$${orgName}${orgMemberId}`;

/**
 * Contexts for the escrowed *signing* private keys' KEKs.
 *
 * Deliberately distinct from getAdminPrivateKeyKekContext. Signing the encryption KEK and the
 * signing KEK under one context makes them interchangeable: both are signed by the same admin
 * key, so a swapped pair verifies. Distinct contexts bind each signature to the slot it belongs
 * in.
 */
export const getAdminSigningPrivateKeyKekContext = async (orgName, orgMemberId) =>
  `adminSigningPrivateKeyKek$${orgName}${orgMemberId}`;

export const getBackdoorSigningPrivateKeyKekContext = async (orgName, orgMemberId) =>
  `backdoorSigningPrivateKeyKek$${orgName}${orgMemberId}`;

export const getGroupKeyContext = async (sessionId, groupName) =>
  `groupKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  )}${groupName}`;

export const getGroupPrivateKeyContext = async (sessionId, groupName) =>
  `groupPrivateKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  )}${groupName}`;

export const getGroupKeyKekContext = async (sessionId, creatorOrgMemberId) =>
  `groupKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${creatorOrgMemberId}`;

export const getGroupPrivateKeyKekContext = async (
  sessionId,
  creatorOrgName,
  creatorOrgMemberId,
) => `groupPrivateKeyKek$${creatorOrgName}${creatorOrgMemberId}`;

export const getGroupPublicKeyContext = async (sessionId, groupName) =>
  `groupPublicKey$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  )}${groupName}`;

export const getGroupTypeContext = async (sessionId) =>
  `groupType$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getUserPublicKeyContext = (email) => `userPublicKey$${email}`;

export const getUserSigningPublicKeyContext = (email) =>
  `userSigningPublicKey$${email}`;

/**
 * Returns the verification tag that matches which private key signed user-scoped data.
 */
/**
 * Returns the verification tag for user-scoped signatures.
 *
 * Signatures are always produced with the user signing key, so the tag is always the signing
 * tag. Falling back to the encryption-key tag would make the verifier resolve the encryption
 * public key and fail (or verify against the wrong identity).
 */
export const getUserSigningVerificationKeyTag = (signingPrivateKey) => {
  if (!signingPrivateKey) {
    throw new Error("User signing private key is required");
  }
  return SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG;
};

export const getUserPrivateKeyOuterKekContext = (email) =>
  `memberPrivateKeyOuterKek$${email}`;

export const getMemberPrivateKeyKekContext = async (sessionId) =>
  `memberPrivateKeyKek$${await getFromStorage(
    sessionId,
    SharedPreferences.ORG_NAME,
  )}${await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)}`;

export const getTransactionIdContext = (
  transactionId,
  transactionStatus,
  transactionLabel,
  orgName,
  orgMemberId,
) => `transactionId$${transactionId}${transactionStatus}${transactionLabel}${orgName}${orgMemberId}`;